import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Switch,
  Tooltip,
} from '@mui/material';
import {
  PersonAdd,
  ArrowBack,
  Delete,
  AdminPanelSettings,
  ManageAccounts,
  CleaningServices,
  Lock,
  Notifications,
  NotificationsOff,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import API from '../api/axios';

function AdminPanel() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: '',
  });
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const roles = [
    { value: 'ADMIN', label: 'Administrator', icon: <AdminPanelSettings />, color: '#1a237e' },
    { value: 'MANAGER', label: 'Manager', icon: <ManageAccounts />, color: '#0d47a1' },
    { value: 'MAID', label: 'Cameristă', icon: <CleaningServices />, color: '#2e7d32' },
  ];

  const fetchUsers = async () => {
    try {
      const response = await API.get('/users');
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCreate = async () => {
    setError('');
    setLoading(true);
    try {
      await API.post('/users', form);
      setSuccess('Utilizator creat cu succes!');
      setDialogOpen(false);
      setForm({ email: '', password: '', firstName: '', lastName: '', role: '' });
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Eroare la crearea utilizatorului');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await API.put(`/users/${id}/toggle-active`);
      fetchUsers();
    } catch (err) {
      console.error('Error toggling user:', err);
    }
  };

  const handleTogglePush = async (id, firstName) => {
    try {
      const response = await API.put(`/users/${id}/toggle-push`);
      const updated = response.data;
      setSuccess(`Notificările pentru ${firstName} au fost ${updated.pushEnabled ? 'activate' : 'dezactivate'}`);
      fetchUsers();
    } catch (err) {
      console.error('Error toggling push:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur vrei să ștergi acest utilizator?')) return;
    try {
      await API.delete(`/users/${id}`);
      fetchUsers();
    } catch (err) {
      console.error('Error deleting user:', err);
    }
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 6) {
      setError('Parola trebuie să aibă minim 6 caractere');
      return;
    }
    try {
      await API.put(`/users/${selectedUser.id}/reset-password`, { password: newPassword });
      setPasswordDialog(false);
      setNewPassword('');
      setSelectedUser(null);
      setSuccess(`Parola pentru ${selectedUser.firstName} a fost resetată!`);
    } catch (err) {
      setError(err.response?.data?.message || 'Eroare la resetarea parolei');
    }
  };

  const getRoleConfig = (role) => roles.find((r) => r.value === role) || roles[2];

  return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Button
                startIcon={<ArrowBack />}
                onClick={() => navigate('/')}
                sx={{ mb: 1, color: 'text.secondary' }}
            >
              Înapoi la Dashboard
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Gestionare utilizatori
            </Typography>
          </Box>
          <Button
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={() => { setDialogOpen(true); setError(''); setSuccess(''); }}
              sx={{
                borderRadius: 2,
                py: 1.2,
                px: 3,
                background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
              }}
          >
            Adaugă utilizator
          </Button>
        </Box>

        {success && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
        )}

        <TableContainer
            component={Paper}
            sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                <TableCell sx={{ fontWeight: 700 }}>Nume</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Rol</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Activ</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Notificări</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Acțiuni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => {
                const roleConfig = getRoleConfig(user.role);
                return (
                    <TableRow
                        key={user.id}
                        sx={{
                          '&:hover': { backgroundColor: '#f5f7fa' },
                          opacity: user.active ? 1 : 0.5,
                        }}
                    >
                      <TableCell>
                        <Typography sx={{ fontWeight: 600 }}>
                          {user.firstName} {user.lastName}
                        </Typography>
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Chip
                            icon={roleConfig.icon}
                            label={roleConfig.label}
                            size="small"
                            sx={{
                              backgroundColor: `${roleConfig.color}15`,
                              color: roleConfig.color,
                              fontWeight: 600,
                            }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={user.active ? 'Dezactivează' : 'Activează'}>
                          <Switch
                              checked={user.active}
                              onChange={() => handleToggleActive(user.id)}
                              color="success"
                              size="small"
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={user.pushEnabled ? 'Dezactivează notificările' : 'Activează notificările'}>
                          <Switch
                              checked={user.pushEnabled}
                              onChange={() => handleTogglePush(user.id, user.firstName)}
                              color="info"
                              size="small"
                              icon={<NotificationsOff sx={{ fontSize: 16 }} />}
                              checkedIcon={<Notifications sx={{ fontSize: 16 }} />}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Resetează parola">
                          <IconButton
                              size="small"
                              color="primary"
                              onClick={() => { setSelectedUser(user); setPasswordDialog(true); setError(''); }}
                          >
                            <Lock fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Șterge utilizator">
                          <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(user.id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                );
              })}
              {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      Nu există utilizatori
                    </TableCell>
                  </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Create User Dialog */}
        <Dialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            Adaugă utilizator nou
          </DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

            <TextField
                fullWidth
                label="Prenume"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                required
                sx={{ mb: 2, mt: 1 }}
            />
            <TextField
                fullWidth
                label="Nume"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                required
                sx={{ mb: 2 }}
            />
            <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                sx={{ mb: 2 }}
            />
            <TextField
                fullWidth
                label="Parolă"
                name="password"
                type="password"
                value={form.password}
                onChange={handleChange}
                required
                helperText="Minim 6 caractere"
                sx={{ mb: 2 }}
            />
            <TextField
                fullWidth
                select
                label="Rol"
                name="role"
                value={form.role}
                onChange={handleChange}
                required
            >
              {roles.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {role.icon}
                      {role.label}
                    </Box>
                  </MenuItem>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDialogOpen(false)} color="inherit">
              Anulează
            </Button>
            <Button
                onClick={handleCreate}
                variant="contained"
                disabled={loading}
                sx={{
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
                }}
            >
              {loading ? 'Se creează...' : 'Creează'}
            </Button>
          </DialogActions>
        </Dialog>
        {/* Reset Password Dialog */}
        <Dialog
            open={passwordDialog}
            onClose={() => { setPasswordDialog(false); setNewPassword(''); }}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            Resetare parolă
          </DialogTitle>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Resetare parolă pentru: <strong>{selectedUser?.firstName} {selectedUser?.lastName}</strong>
            </Typography>
            <TextField
                fullWidth
                label="Parolă nouă"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                helperText="Minim 6 caractere"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => { setPasswordDialog(false); setNewPassword(''); }} color="inherit">
              Anulează
            </Button>
            <Button
                onClick={handleResetPassword}
                variant="contained"
                sx={{
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
                }}
            >
              Resetează
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
}

export default AdminPanel;